import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, ChevronRight, CircleCheck, Euro, FileCheck, FileSignature, PackageCheck, Pen, Plus, Printer, Truck, Upload, X } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { vociDi } from "@/lib/accessi";
import { eur } from "@/lib/formato";
import { apriVersione } from "@/lib/documenti";
import {
  AZIONE_VERSO,
  FORMULE_SALDO,
  ORDINE_STATI,
  FIRMATI,
  PROPOSTA,
  RATE,
  STATI_CONTRATTO,
  centesimiDaApi,
  centesimiDaTesto,
  centesimiInApi,
  centesimiInCampo,
  dataLunga,
  messaggioErrore,
  meseConsegna,
  oggiRoma,
  propostaPiano,
  statoContratto,
  TONO,
} from "@/lib/soldi";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { StatusBadge } from "@/components/ui/status-badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { EmptyState, ErrorState, ListSkeleton } from "@/components/ui/states";
import { useMedia } from "@/hooks/use-media";
import { euro } from "@/lib/format";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import PageHeader from "@/components/common/PageHeader";
import CampoData from "@/components/common/CampoData";

/* Il contratto resta di carta (call del 23-09): qui si prepara coi numeri già
   dentro, si stampa, si segna firmato. I numeri li scrive il database dal
   preventivo; la pagina non ne calcola uno, tranne il saldo del piano mentre lo
   si scrive — e il database lo ricontrolla al centesimo. */


function Errore({ children, onRiprova }) {
  return (
    <ErrorState title="Non riesco a leggere i contratti" actions={onRiprova ? <Button onClick={onRiprova}>Riprova</Button> : null} data-testid="contratti-errore">
      {children}
    </ErrorState>
  );
}

function ErroreInline({ children }) {
  return (
    <div role="alert" className="rounded-xl border border-danger-line bg-danger-bg px-4 py-3 text-danger-ink">
      {children}
    </div>
  );
}

function SezioneCard({ titolo, descrizione, azioni, children, testId, className }) {
  return (
    <section className={cn("rounded-xl border border-line bg-surface p-4 md:p-5", className)} data-testid={testId}>
      <div className="flex flex-wrap items-start justify-between gap-inside">
        <div className="min-w-0">
          <h2 className="font-display text-h3 text-ink-strong">{titolo}</h2>
          {descrizione ? <p className="mt-1 text-ink-2">{descrizione}</p> : null}
        </div>
        {azioni ? <div className="flex shrink-0 flex-wrap gap-inside">{azioni}</div> : null}
      </div>
      <div className="mt-group">{children}</div>
    </section>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
   Elenco
   ══════════════════════════════════════════════════════════════════════════ */

function NuovoContrattoDialog({ open, onOpenChange, onCreato }) {
  const [righe, setRighe] = useState(null);
  const [scelto, setScelto] = useState(null);
  const [busy, setBusy] = useState(false);
  const [errore, setErrore] = useState(null);

  useEffect(() => {
    if (!open) return;
    setScelto(null);
    setRighe(null);
    setErrore(null);
    api
      .get("/contratti/preventivi-disponibili")
      .then((r) => setRighe(r.data.righe))
      .catch((e) => setErrore(messaggioErrore(e)));
  }, [open]);

  const crea = async () => {
    if (!scelto) return;
    setBusy(true);
    try {
      const r = await api.post("/contratti", { preventivo_id: scelto });
      onCreato(r.data.id);
    } catch (e) {
      toast.error(messaggioErrore(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="nuovo-contratto-modal" className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Nuovo contratto</DialogTitle>
          <DialogDescription>
            Per chi conferma in negozio. Scegli il preventivo: cliente, prezzo e configurazione entrano da soli.
          </DialogDescription>
        </DialogHeader>
        {errore ? <ErroreInline>{errore}</ErroreInline> : null}
        {righe === null && !errore ? (
          <div className="grid gap-inside">
            <Skeleton className="h-14 w-full" />
            <Skeleton className="h-14 w-full" />
          </div>
        ) : null}
        {righe && righe.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            Nessun preventivo senza contratto. Un contratto nasce da un preventivo: prima si prepara quello.
          </p>
        ) : null}
        {righe && righe.length ? (
          <div role="radiogroup" aria-label="Preventivo" className="grid gap-inside" data-testid="nuovo-contratto-preventivi">
            {righe.map((p) => {
              const attivo = scelto === p.id;
              return (
                <button
                  key={p.id}
                  type="button"
                  role="radio"
                  aria-checked={attivo}
                  onClick={() => setScelto(p.id)}
                  data-testid={`preventivo-${p.id}`}
                  className={cn(
                    "grid min-h-11 gap-micro rounded-lg border px-group py-inside text-left transition-colors",
                    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
                    attivo ? "border-primary bg-brand-subtle" : "border-border bg-card hover:bg-accent",
                  )}
                >
                  <span className="font-medium text-foreground">{p.cliente}</span>
                  <span className="text-xs text-muted-foreground">
                    {p.titolo}
                    {p.numero ? ` · n. ${p.numero}` : ""}
                    {p.inviato_at ? " · inviato" : ""}
                  </span>
                </button>
              );
            })}
          </div>
        ) : null}
        <DialogFooter className="gap-inside">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Annulla
          </Button>
          <Button type="button" onClick={crea} disabled={!scelto || busy} data-testid="nuovo-contratto-crea">
            {busy ? "Creo…" : "Crea il contratto"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* Il prossimo passo di un contratto, in una riga. Le rate (in ritardo, in scadenza) le sa solo chi vede gli
   incassi: al venditore il passo viene dallo stato, senza inventare quello che non può leggere. */
function prossimoPasso(r, rata) {
  if (rata?.stato === "in_ritardo") {
    return { testo: `${rata.causale} in ritardo da ${rata.giorni_ritardo === 1 ? "1 giorno" : `${rata.giorni_ritardo} giorni`} · ${eur(rata.residuo)}`, icona: AlertTriangle, tono: "danger" };
  }
  if (r.stato === "bozza") {
    if (r.configurazione_da_scegliere) return { testo: "Scegli la configurazione", icona: Pen, tono: "warning" };
    if (!r.piano) return { testo: "Manca il piano di pagamento", icona: Pen, tono: "warning" };
    return { testo: "Configurazione e piano a posto: segnalo pronto da firmare", icona: Pen, tono: "warning" };
  }
  if (r.stato === "da_firmare") return { testo: "Stampalo e fissa la firma in sede", icona: FileSignature, tono: "warning" };
  if (r.stato === "annullato") return { testo: "Annullato", icona: X, tono: "neutral" };
  if (rata) {
    const quando = rata.scadenza ? ` entro il ${dataLunga(rata.scadenza)}` : " (data da fissare)";
    return { testo: `${rata.causale} ${eur(rata.residuo)}${quando}`, icona: Euro, tono: "neutral" };
  }
  if (r.stato === "firmato") return { testo: "Firmato", icona: Euro, tono: "neutral" };
  if (r.stato === "in_produzione") return { testo: "In produzione", icona: PackageCheck, tono: "neutral" };
  if (r.stato === "consegnato") return { testo: "Consegnato", icona: Truck, tono: "neutral" };
  return { testo: "Concluso", icona: CircleCheck, tono: "neutral" };
}

const TONO_PASSO = { danger: "text-danger-ink font-semibold", warning: "text-warning-ink", neutral: "text-ink" };
const COLONNE_CONTRATTI = "grid-cols-[minmax(0,1.6fr)_124px_minmax(0,1.9fr)_190px_136px_24px]";

/* La rata più urgente di ogni contratto: prima la più in ritardo, poi la scadenza più vicina, poi quelle senza data. */
function rataPerContratto(righe) {
  const per = new Map();
  const peso = (x) => (x.stato === "in_ritardo" ? -1e6 + -x.giorni_ritardo : x.scadenza ? Date.parse(x.scadenza) / 1e5 : 1e12);
  for (const x of righe || []) {
    const gia = per.get(x.contratto_id);
    if (!gia || peso(x) < peso(gia)) per.set(x.contratto_id, x);
  }
  return per;
}

function RigaContratto({ r, largo, apri, vediIncassi, rata }) {
  const s = statoContratto(r.stato);
  const passo = prossimoPasso(r, rata);
  const Icona = passo.icona;
  const sub = [
    r.numero ? `n. ${r.numero}` : "bozza",
    FIRMATI.has(r.stato) && r.data_firma ? `firmato il ${dataLunga(r.data_firma)}` : null,
  ].filter(Boolean);
  const totale = Number(r.totale) || 0;
  const incassato = r.incassato !== null && r.incassato !== undefined ? Number(r.incassato) : null;
  const quota = totale > 0 && incassato !== null ? Math.min(100, Math.round((incassato / totale) * 100)) : 0;
  const nome = (
    <div className="flex min-w-0 flex-wrap items-center gap-inside">
      <Link
        to={`/contratti?c=${r.id}`}
        className="truncate font-semibold text-ink-strong hover:underline max-md:py-3"
        onClick={(e) => e.stopPropagation()}
        data-testid={`contratto-link-${r.id}`}
      >
        {r.cliente}
      </Link>
      {r.esterno ? <StatusBadge data-testid={`contratto-fuori-${r.id}`}>Fatto fuori</StatusBadge> : null}
    </div>
  );
  const passoEl = (
    <span className={cn("flex items-start gap-2", TONO_PASSO[passo.tono])}>
      <Icona className="mt-0.5 size-4 shrink-0" aria-hidden />
      <span>{passo.testo}</span>
    </span>
  );
  const incassatoEl =
    vediIncassi && FIRMATI.has(r.stato) && incassato !== null ? (
      <div>
        <span className="font-bold tabular-nums text-ink-strong">{eur(incassato)}</span>
        <span className="text-ink-2"> di {eur(totale)}</span>
        <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-surface-3" aria-hidden>
          <div className="h-full rounded-full bg-brand" style={{ width: `${quota}%` }} />
        </div>
      </div>
    ) : (
      <span className="text-ink-2">{r.totale ? `${eur(r.totale)}` : "da definire"}</span>
    );
  if (!largo) {
    return (
      <li
        data-testid={`contratto-row-${r.id}`}
        onClick={() => apri(r.id)}
        className="flex cursor-pointer flex-col gap-2.5 border-b border-line-subtle bg-surface px-4 py-3.5 last:border-b-0"
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            {nome}
            <p className="text-small text-ink-2">{sub.join(" · ")}</p>
          </div>
          <StatusBadge tone={TONO[s.pill]}>{s.label}</StatusBadge>
        </div>
        {passoEl}
        <div className="flex items-end justify-between gap-3">
          <div className="min-w-0 flex-1">{incassatoEl}</div>
          {r.venditore ? <span className="shrink-0 text-small text-ink-2">{r.venditore}</span> : null}
        </div>
      </li>
    );
  }
  return (
    <div
      role="row"
      data-testid={`contratto-row-${r.id}`}
      onClick={() => apri(r.id)}
      className={cn("grid cursor-pointer items-center gap-4 border-b border-line-subtle bg-surface px-4 py-3 transition-colors duration-150 ease-enter last:border-b-0 hover:bg-surface-2", COLONNE_CONTRATTI)}
    >
      <div role="cell" className="min-w-0">
        {nome}
        <p className="truncate text-small text-ink-2">{sub.join(" · ")}</p>
      </div>
      <span role="cell"><StatusBadge tone={TONO[s.pill]}>{s.label}</StatusBadge></span>
      <div role="cell">{passoEl}</div>
      <div role="cell">{incassatoEl}</div>
      <span role="cell" className="truncate text-ink-2">{r.venditore || "—"}</span>
      <ChevronRight role="cell" className="size-4 text-ink-3" aria-hidden />
    </div>
  );
}

function ElencoContratti({ apri }) {
  const { profile } = useAuth();
  const ruolo = profile?.ruolo;
  const [params, setParams] = useSearchParams();
  const stato = ORDINE_STATI.includes(params.get("stato")) ? params.get("stato") : "tutti";
  const [dati, setDati] = useState(null);
  const [rate, setRate] = useState(null);
  const [errore, setErrore] = useState(null);
  const [nuovo, setNuovo] = useState(false);
  const [cerca, setCerca] = useState("");
  const largo = useMedia("(min-width: 768px)");
  const puoCreare = ruolo === "titolare" || ruolo === "venditore";
  // Gli incassi li legge chi ha il ruolo E la sezione «Fatture e incassi» accesa: con la sezione spenta le rate
  // si vedono ma i soldi no, e «in ritardo» sarebbe una frase inventata.
  const vediIncassi = (ruolo === "titolare" || ruolo === "amministrazione") && vociDi(ruolo, profile?.sezioni_spente).includes("fatture-incassi");

  const carica = useCallback(() => {
    api
      .get("/contratti")
      .then((r) => {
        setDati(r.data);
        setErrore(null);
      })
      .catch((e) => setErrore(messaggioErrore(e)));
  }, []);
  useEffect(carica, [carica]);

  // Le rate aperte (in ritardo, in scadenza) le leggono solo titolare e amministrazione: è la stessa lettura
  // di Fatture e incassi, con la sua RLS. Se non risponde, l'elenco c'è lo stesso, senza i ritardi.
  useEffect(() => {
    if (!vediIncassi) return;
    Promise.resolve(api.get("/incassi"))
      .then((r) => setRate(rataPerContratto(r.data?.righe)))
      .catch(() => setRate(new Map()));
  }, [vediIncassi]);

  const cambia = (s) => {
    const p = new URLSearchParams(params);
    if (s === "tutti") p.delete("stato");
    else p.set("stato", s);
    setParams(p, { replace: true });
  };

  const tutte = dati?.righe || [];
  const conteggi = dati?.conteggi || {};
  const valori = {};
  for (const r of tutte) valori[r.stato] = (valori[r.stato] || 0) + (Number(r.totale) || 0);
  const inCorso = tutte.filter((r) => r.stato !== "annullato");
  const q = cerca.trim().toLowerCase();
  const base = stato === "tutti" ? inCorso : tutte.filter((r) => r.stato === stato);
  const filtrate = base.filter((r) => !q || `${r.cliente || ""} ${r.numero || ""}`.toLowerCase().includes(q));
  // Prima i ritardi (il più lungo in cima), poi gli altri nell'ordine del server.
  const ritardo = (r) => (rate?.get(r.id)?.stato === "in_ritardo" ? rate.get(r.id).giorni_ritardo : -1);
  const righe = filtrate
    .map((r, i) => [r, i])
    .sort((a, b) => ritardo(b[0]) - ritardo(a[0]) || a[1] - b[1])
    .map(([r]) => r);

  const FASI = [
    { id: "tutti", label: "In corso", n: inCorso.length, v: inCorso.reduce((t, r) => t + (Number(r.totale) || 0), 0) },
    ...ORDINE_STATI.map((s) => ({ id: s, label: STATI_CONTRATTO[s].tab, n: conteggi[s] || 0, v: valori[s] || 0 })),
  ];

  return (
    <section data-testid="page-contratti" className="grid grid-cols-[minmax(0,1fr)] gap-card">
      <PageHeader
        title="Contratti"
        description="Dal preventivo confermato alla firma in sede, poi fino al montaggio. Si firma su carta: qui si prepara e si segue."
        actions={
          <>
            <label className="relative block w-full sm:w-64">
              <span className="sr-only">Cerca</span>
              <Input type="search" value={cerca} onChange={(e) => setCerca(e.target.value)} placeholder="Cliente o numero" data-testid="contratti-cerca" />
            </label>
            {puoCreare ? (
              <Button onClick={() => setNuovo(true)} data-testid="btn-nuovo-contratto">
                <Plus />
                Nuovo contratto
              </Button>
            ) : null}
          </>
        }
      />
      <NuovoContrattoDialog open={nuovo} onOpenChange={setNuovo} onCreato={(id) => { setNuovo(false); apri(id); }} />

      <nav aria-label="Filtra per stato" className="-mx-1 flex max-w-full gap-2 overflow-x-auto px-1 pb-1 md:flex-wrap md:overflow-visible" data-testid="contratti-stati">
        {FASI.map((f) => {
          const on = stato === f.id;
          return (
            <button
              key={f.id}
              type="button"
              aria-pressed={on}
              onClick={() => cambia(f.id)}
              data-testid={`stato-${f.id}`}
              className={cn(
                "flex min-h-11 shrink-0 flex-col items-start justify-center rounded-lg border px-3.5 py-2 text-left transition-colors duration-150 ease-enter",
                "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand",
                on ? "border-2 border-brand bg-brand-subtle" : "border-line bg-surface hover:bg-surface-2",
              )}
            >
              <span className={cn("flex items-center gap-2", on ? "font-bold text-brand-ink" : "font-medium text-ink-2")}>
                {f.label}
                {f.n || f.id === "tutti" ? (
                  <span
                    className={cn(
                      "inline-flex h-5 min-w-5 items-center justify-center rounded-xs px-1.5 text-caption font-bold tabular-nums",
                      f.id === "da_firmare" && f.n ? "bg-warning-bg text-warning-ink" : on ? "bg-brand text-on-brand" : "bg-surface-3 text-ink-2",
                    )}
                    data-testid={`conteggio-${f.id}`}
                  >
                    {f.n}
                  </span>
                ) : null}
              </span>
              {f.v > 0 && f.id !== "annullato" ? <span className="text-small tabular-nums text-ink-2">{euro(f.v)}</span> : null}
            </button>
          );
        })}
      </nav>

      {errore ? <Errore onRiprova={carica}>{errore} È solo una lettura: non si è perso niente.</Errore> : null}

      {dati === null && !errore ? <ListSkeleton rows={5} label="Carico i contratti…" /> : null}

      {dati && righe.length === 0 ? (
        <div data-testid="contratti-vuoto">
          <EmptyState
            icon={FileCheck}
            title={q ? `Nessun risultato per «${cerca.trim()}»` : stato === "tutti" ? "Nessun contratto in corso" : `Nessun contratto: ${STATI_CONTRATTO[stato].tab.toLowerCase()}`}
            actions={
              q ? (
                <Button variant="outline" onClick={() => setCerca("")}>Cancella la ricerca</Button>
              ) : stato === "tutti" && puoCreare ? (
                <Button onClick={() => setNuovo(true)}>
                  <Plus />
                  Nuovo contratto
                </Button>
              ) : null
            }
          >
            {q
              ? "Cerca per nome del cliente o per numero del contratto."
              : stato === "tutti"
                ? "Un contratto nasce quando il cliente preme «Confermo» sul preventivo, oppure da qui, per chi conferma in negozio."
                : "Quando un contratto arriva a questo punto, compare qui."}
          </EmptyState>
        </div>
      ) : null}

      {righe.length ? (
        <div className="overflow-hidden rounded-xl border border-line bg-surface" data-testid="contratti-tabella">
          {largo ? (
            <div role="table" aria-label="Contratti">
              <div role="row" className={cn("grid items-center gap-4 border-b border-line bg-surface-2 px-4 py-2.5 text-small font-semibold text-ink-2", COLONNE_CONTRATTI)}>
                <span role="columnheader">Cliente</span>
                <span role="columnheader">Stato</span>
                <span role="columnheader">Prossimo passo</span>
                <span role="columnheader">{vediIncassi ? "Incassato" : "Totale"}</span>
                <span role="columnheader">Venditore</span>
                <span role="columnheader" className="sr-only">Apri</span>
              </div>
              <div role="rowgroup">
                {righe.map((r) => (
                  <RigaContratto key={r.id} r={r} largo apri={apri} vediIncassi={vediIncassi} rata={rate?.get(r.id)} />
                ))}
              </div>
            </div>
          ) : (
            <ul>
              {righe.map((r) => (
                <RigaContratto key={r.id} r={r} largo={false} apri={apri} vediIncassi={vediIncassi} rata={rate?.get(r.id)} />
              ))}
            </ul>
          )}
        </div>
      ) : null}
    </section>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
   Dettaglio
   ══════════════════════════════════════════════════════════════════════════ */

function FirmaDialog({ open, onOpenChange, onFirmato, busy }) {
  const [data, setData] = useState(oggiRoma());
  useEffect(() => {
    if (open) setData(oggiRoma());
  }, [open]);
  const futura = data > oggiRoma();
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="firma-modal">
        <form
          className="grid gap-card"
          onSubmit={(e) => {
            e.preventDefault();
            if (data && !futura) onFirmato(data);
          }}
        >
          <DialogHeader>
            <DialogTitle>Il cliente ha firmato</DialogTitle>
            <DialogDescription>
              Scrivi il giorno della firma in sede. Dopo, prezzo e configurazione non si cambiano più.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-inside">
            <Label htmlFor="data-firma">Data di firma</Label>
            <CampoData id="data-firma" value={data} max={oggiRoma()} onChange={(e) => setData(e.target.value)} testId="firma-data" />
            {futura ? <p className="text-small text-danger-ink">La data di firma non può essere nel futuro.</p> : null}
          </div>
          <DialogFooter className="gap-inside">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Annulla
            </Button>
            <Button type="submit" disabled={!data || futura || busy} data-testid="firma-conferma">
              {busy ? "Salvo…" : "Segna come firmato"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function PassoStato({ d, onCambia, busy, className }) {
  const k = d.contratto;
  const [firma, setFirma] = useState(false);
  const [annulla, setAnnulla] = useState(false);
  const stati = d.puo.stati || [];
  const avanti = stati.filter((s) => !["annullato", "bozza"].includes(s));
  const indietro = stati.filter((s) => s === "bozza");
  const perche =
    k.stato === "bozza"
      ? !k.configurazione_id
        ? "Scegli prima la configurazione."
        : !d.piano.length
          ? "Scrivi prima il piano di pagamento."
          : null
      : null;

  const vai = (s) => {
    if (s === "firmato") setFirma(true);
    else if (s === "annullato") setAnnulla(true);
    else onCambia({ stato: s });
  };

  const s = statoContratto(k.stato);
  return (
    <SezioneCard
      titolo="A che punto è"
      testId="contratto-stato"
      className={className}
      azioni={<StatusBadge tone={TONO[s.pill]}>{s.label}</StatusBadge>}
    >
      <div className="grid gap-group">
        {FIRMATI.has(k.stato) && k.data_firma ? (
          <p className="text-foreground">
            Firmato il <strong>{dataLunga(k.data_firma)}</strong>.
          </p>
        ) : null}
        {k.stato === "bozza" ? (
          <p className="text-ink-2">
            Quando configurazione e piano sono a posto, stampalo e portalo alla firma in sede.
          </p>
        ) : null}
        {k.stato === "da_firmare" ? (
          <p className="text-ink-2">Stampato e pronto. Dopo la firma, segnalo qui e carica la scansione del firmato.</p>
        ) : null}
        {avanti.length || indietro.length ? (
          <div className="flex flex-wrap gap-inside">
            {avanti.map((st) => (
              <Button
                key={st}
                onClick={() => vai(st)}
                disabled={busy || (st === "da_firmare" && Boolean(perche))}
                data-testid={`azione-${st}`}
              >
                {st === "firmato" ? <FileSignature /> : null}
                {AZIONE_VERSO[st]}
              </Button>
            ))}
            {indietro.map((st) => (
              <Button key={st} variant="outline" onClick={() => vai(st)} disabled={busy} data-testid={`azione-${st}`}>
                {AZIONE_VERSO[st]}
              </Button>
            ))}
          </div>
        ) : null}
        {perche && avanti.includes("da_firmare") ? <p className="text-small text-warning-ink">{perche}</p> : null}
        {FIRMATI.has(k.stato) && !k.esterno ? (
          <Button asChild variant="outline" className="h-11 sm:h-9 justify-self-start" data-testid="carica-firmato">
            <Link to={d.documenti_url}>
              <Upload />
              Carica il firmato
            </Link>
          </Button>
        ) : null}
        {stati.includes("annullato") ? (
          <button
            type="button"
            onClick={() => vai("annullato")}
            className="min-h-11 justify-self-start text-ink-2 underline-offset-4 hover:text-danger-ink hover:underline"
            data-testid="azione-annullato"
          >
            Annulla il contratto
          </button>
        ) : null}
      </div>
      <FirmaDialog
        open={firma}
        onOpenChange={setFirma}
        busy={busy}
        onFirmato={async (data) => {
          const ok = await onCambia({ stato: "firmato", data_firma: data });
          if (ok) setFirma(false);
        }}
      />
      <AlertDialog open={annulla} onOpenChange={setAnnulla}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Annullare il contratto n. {k.numero}?</AlertDialogTitle>
            <AlertDialogDescription>
              Resta nell'elenco degli annullati, col suo numero. Il titolare o l'amministrazione possono riportarlo in bozza.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Lascialo com'è</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => onCambia({ stato: "annullato" })}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="annulla-conferma"
            >
              Annulla il contratto
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </SezioneCard>
  );
}

function Prezzo({ d, ruolo, onScegli, busy, className }) {
  const k = d.contratto;
  const opzioni = d.configurazioni;
  const scelta = d.puo.scegliere_configurazione && opzioni && opzioni.length > 1;
  return (
    <SezioneCard
      titolo="Prezzo e configurazione"
      descrizione={k.esterno ? "Registrato da un contratto fatto fuori" : k.preventivo?.titolo ? `Dal preventivo «${k.preventivo.titolo}»` : "Dal preventivo accettato"}
      testId="contratto-prezzo"
      className={className}
    >
      <div className="grid gap-group">
        <div>
          <p className="font-figure text-display tabular-nums text-ink-strong" data-testid="contratto-totale">
            {k.totale ? eur(k.totale) : "Da definire"}
          </p>
          <p className="mt-1 text-ink-2">
            {k.totale ? `IVA inclusa${k.esterno ? "" : ` · ${k.configurazione || "configurazione"}`}` : "Il prezzo arriva quando si sceglie la configurazione."}
          </p>
        </div>

        {scelta ? (
          <div className="grid gap-inside">
            <p className="text-sm font-medium text-foreground">
              {k.configurazione_id ? "Configurazione scelta dal cliente" : "Quale configurazione ha scelto il cliente?"}
            </p>
            <div role="radiogroup" aria-label="Configurazione" className="grid gap-inside" data-testid="scelta-configurazione">
              {opzioni.map((o) => {
                const attiva = o.id === k.configurazione_id;
                return (
                  <button
                    key={o.id}
                    type="button"
                    role="radio"
                    aria-checked={attiva}
                    disabled={busy || attiva}
                    onClick={() => onScegli(o.id)}
                    data-testid={`configurazione-${o.id}`}
                    className={cn(
                      "flex min-h-11 items-center justify-between gap-group rounded-lg border px-group py-inside text-left transition-colors",
                      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-default",
                      attiva ? "border-primary bg-brand-subtle" : "border-border bg-card hover:bg-accent",
                    )}
                  >
                    <span className="flex min-w-0 items-center gap-inside">
                      <span
                        className={cn(
                          "grid h-4 w-4 shrink-0 place-items-center rounded-full border",
                          attiva ? "border-primary" : "border-input",
                        )}
                        aria-hidden
                      >
                        {attiva ? <span className="h-2 w-2 rounded-full bg-primary" /> : null}
                      </span>
                      <span className="truncate font-medium text-foreground">{o.etichetta}</span>
                    </span>
                    <span className="whitespace-nowrap text-sm tabular-nums text-muted-foreground">{o.totale ? eur(o.totale) : "—"}</span>
                  </button>
                );
              })}
            </div>
          </div>
        ) : null}
        {!k.configurazione_id && ruolo === "amministrazione" ? (
          <p className="text-sm text-warning-foreground">La configurazione la sceglie il venditore o il titolare.</p>
        ) : null}

        {d.economia ? (
          <div className="rounded-lg bg-surface-2 px-group py-inside" data-testid="contratto-netto">
            <span className="text-muted-foreground">Netto per l'azienda: </span>
            <strong className="tabular-nums text-foreground">{d.economia.netto ? eur(d.economia.netto) : "—"}</strong>
            <span className="text-muted-foreground">
              {d.economia.costo_imponibile ? ` · costo ${eur(d.economia.costo_imponibile)} IVA esclusa` : ""} · lo vedi solo tu
            </span>
          </div>
        ) : null}
      </div>
    </SezioneCard>
  );
}

function pianoIniziale(d) {
  const per = Object.fromEntries((d.piano || []).map((r) => [r.tipo, r]));
  const campo = (r) => (r ? centesimiInCampo(centesimiDaApi(r.importo)) : null);
  const conDieci = Boolean(per.terzo_acconto);
  return {
    conDieci,
    // Quando si paga il 10% lo si sceglie contratto per contratto: nessun default (D11).
    formula: conDieci ? d.contratto.saldo_modalita : null,
    primo: campo(per.primo_acconto) ?? "",
    primoScad: per.primo_acconto?.scadenza || "",
    // null = la proposta; "" = nessuna rata dopo il rilievo (un piano salvato senza).
    secondo: d.piano?.length ? campo(per.secondo_acconto) ?? "" : null,
    secondoScad: per.secondo_acconto?.scadenza || "",
    terzoScad: per.terzo_acconto?.scadenza || "",
    finale: conDieci ? campo(per.saldo) : null,
    saldoScad: per.saldo?.scadenza || "",
    consegna: (d.contratto.consegna_voluta || "").slice(0, 7),
  };
}

/* I mesi da proporre per la consegna voluta: da questo a due anni avanti, più quello già scritto. */
function mesiDiConsegna(scritto) {
  const [a, m] = oggiRoma().split("-").map(Number);
  const mesi = Array.from({ length: 24 }, (_, i) => {
    const n = m - 1 + i;
    return `${a + Math.floor(n / 12)}-${String((n % 12) + 1).padStart(2, "0")}`;
  });
  return scritto && !mesi.includes(scritto) ? [scritto, ...mesi] : mesi;
}

function Scelta({ legenda, valore, opzioni, onScegli, testId }) {
  return (
    <fieldset className="grid gap-inside">
      <legend className="mb-inside text-sm font-medium text-foreground">{legenda}</legend>
      <div role="radiogroup" className="grid gap-inside sm:grid-cols-2">
        {opzioni.map(([codice, label]) => {
          const attiva = valore === codice;
          return (
            <button
              key={codice}
              type="button"
              role="radio"
              aria-checked={attiva}
              onClick={() => onScegli(codice)}
              data-testid={`${testId}-${codice}`}
              className={cn(
                "flex min-h-11 items-center gap-inside rounded-lg border px-group py-inside text-left text-sm transition-colors",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
                attiva ? "border-primary bg-brand-subtle font-medium text-brand-subtle-foreground" : "border-border bg-card text-foreground hover:bg-accent",
              )}
            >
              <span className={cn("grid h-4 w-4 shrink-0 place-items-center rounded-full border", attiva ? "border-primary" : "border-input")} aria-hidden>
                {attiva ? <span className="h-2 w-2 rounded-full bg-primary" /> : null}
              </span>
              {label}
            </button>
          );
        })}
      </div>
    </fieldset>
  );
}

/* `onSubmit` consegna `{ saldo_modalita, righe, consegna_voluta }` senza salvare: lo usa la finestra «Fatto fuori»,
   che manda il piano insieme al contratto. Senza, salva da sola col PUT, come sempre. */
export function PianoForm({ d, onSalvato, onAnnulla, onSubmit, etichettaSalva = "Salva il piano", etichettaAnnulla = "Annulla" }) {
  const [v, setV] = useState(() => pianoIniziale(d));
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setV((x) => ({ ...x, [k]: e.target.value }));
  const totale = d.contratto.totale;
  const tot = centesimiDaApi(totale);
  const primo = centesimiDaTesto(v.primo);
  const proposta = propostaPiano(tot, primo);
  const testoSecondo = v.secondo ?? (proposta.dopoRilievo ? centesimiInCampo(proposta.dopoRilievo) : "");
  const testoFinale = v.finale ?? centesimiInCampo(proposta.finale);
  const secondo = testoSecondo.trim() ? centesimiDaTesto(testoSecondo) : 0;
  const finale = v.conDieci ? centesimiDaTesto(testoFinale) : 0;
  // Quello che resta si paga prima della consegna: è il saldo, o col 10% finale la rata prima.
  const resto = [tot, primo, secondo, finale].includes(null) ? null : tot - primo - secondo - finale;
  const problema = !totale
    ? "Prima serve il prezzo: scegli la configurazione."
    : !(primo > 0)
      ? "Scrivi l'acconto alla firma."
      : secondo === null
        ? "L'importo dopo il rilievo non è valido."
        : v.conDieci && !(finale > 0)
          ? "Il 10% finale non è un importo valido."
          : v.conDieci && !v.formula
            ? "Scegli quando si paga il 10%: prima della consegna o a montaggio finito."
            : resto === null
              ? "Controlla gli importi."
              : resto <= 0
                ? "Gli acconti arrivano al totale o lo superano: prima della consegna deve restare qualcosa."
                : null;

  const salva = async (e) => {
    e.preventDefault();
    if (problema) return;
    const rata = (tipo, cent, scadenza, causale) => ({ tipo, importo: centesimiInApi(cent), scadenza: scadenza || null, causale });
    const righe = [rata("primo_acconto", primo, v.primoScad, "Acconto alla firma")];
    if (secondo > 0) righe.push(rata("secondo_acconto", secondo, v.secondoScad, "Dopo il rilievo"));
    if (v.conDieci) {
      righe.push(rata("terzo_acconto", resto, v.terzoScad, "Prima della consegna"));
      righe.push(rata("saldo", finale, v.saldoScad, FORMULE_SALDO[v.formula].causale));
    } else {
      righe.push(rata("saldo", resto, v.saldoScad, FORMULE_SALDO.prima_consegna.causale));
    }
    const saldo_modalita = v.conDieci ? v.formula : "prima_consegna";
    if (onSubmit) {
      onSubmit({ saldo_modalita, righe, consegna_voluta: v.consegna });
      return;
    }
    setBusy(true);
    try {
      const r = await api.put(`/contratti/${d.contratto.id}/piano`, { saldo_modalita, righe, consegna_voluta: v.consegna });
      toast.success("Piano di pagamento salvato");
      onSalvato(r.data);
    } catch (err) {
      toast.error(messaggioErrore(err));
    } finally {
      setBusy(false);
    }
  };

  const riga = (id, etichetta, importo, onImporto, scad, onScad, nota) => (
    <div className="grid gap-inside sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] sm:gap-group">
      <div className="grid content-start gap-inside">
        <Label htmlFor={`${id}-importo`}>{etichetta}</Label>
        <Input
          id={`${id}-importo`}
          inputMode="decimal"
          autoComplete="off"
          placeholder="0,00"
          value={importo}
          onChange={onImporto}
          aria-describedby={nota ? `${id}-nota` : undefined}
          data-testid={`piano-${id}`}
        />
        {nota ? <p id={`${id}-nota`} className="text-xs text-muted-foreground">{nota}</p> : null}
      </div>
      <div className="grid content-start gap-inside">
        <Label htmlFor={`${id}-scadenza`}>Scadenza prevista</Label>
        <CampoData id={`${id}-scadenza`} value={scad} onChange={onScad} testId={`piano-${id}-scadenza`} />
      </div>
    </div>
  );

  const calcolata = (id, etichetta, scad, onScad) => (
    <div className="grid gap-inside sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] sm:gap-group">
      <div className="grid content-start gap-inside">
        <span className="text-sm font-medium text-foreground">{etichetta}</span>
        <p className="flex h-9 items-center rounded-md bg-muted px-3 text-sm tabular-nums text-foreground" data-testid={`piano-${id}`}>
          {resto !== null && resto > 0 ? eur(centesimiInApi(resto)) : "—"}
        </p>
        <p className="text-xs text-muted-foreground">Si calcola da solo: il totale meno le altre rate.</p>
      </div>
      <div className="grid content-start gap-inside">
        <Label htmlFor={`${id}-scadenza`}>
          Scadenza prevista <span className="font-normal text-muted-foreground">(se si sa già)</span>
        </Label>
        <CampoData id={`${id}-scadenza`} value={scad} onChange={onScad} testId={`piano-${id}-scadenza`} />
      </div>
    </div>
  );

  return (
    <form onSubmit={salva} className="grid gap-card" data-testid="piano-form">
      <Scelta
        legenda="Il piano"
        valore={v.conDieci ? "col_dieci" : "quasi_tutti"}
        opzioni={[["quasi_tutti", "Acconto, 30% e saldo prima della consegna"], ["col_dieci", "Col 10% finale"]]}
        onScegli={(c) => setV((x) => ({ ...x, conDieci: c === "col_dieci" }))}
        testId="piano-tipo"
      />

      {riga("primo", "Acconto alla firma", v.primo, set("primo"), v.primoScad, set("primoScad"))}
      {riga("secondo", "Dopo il rilievo", testoSecondo, set("secondo"), v.secondoScad, set("secondoScad"),
        `Proposto: il ${PROPOSTA.dopoRilievo}% del totale${PROPOSTA.comprendeAcconto ? " meno l'acconto" : ""}. Vuoto = nessuna rata dopo il rilievo.`)}
      {v.conDieci ? (
        <>
          {calcolata("terzo", "Prima della consegna", v.terzoScad, set("terzoScad"))}
          {riga("finale", `Il ${PROPOSTA.finale}% finale`, testoFinale, set("finale"), v.saldoScad, set("saldoScad"),
            `Proposto: il ${PROPOSTA.finale}% del totale.`)}
          <Scelta
            legenda={`Quando si paga il ${PROPOSTA.finale}%`}
            valore={v.formula}
            opzioni={[["prima_consegna", "Prima della consegna"], ["dopo_montaggio", "A montaggio finito"]]}
            onScegli={(c) => setV((x) => ({ ...x, formula: c }))}
            testId="formula"
          />
        </>
      ) : (
        calcolata("saldo", "Saldo prima della consegna", v.saldoScad, set("saldoScad"))
      )}

      <div className="grid gap-inside sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] sm:gap-group">
        <div className="grid content-start gap-inside">
        <Label htmlFor="piano-consegna">
          Consegna voluta dal cliente <span className="font-normal text-muted-foreground">(facoltativa)</span>
        </Label>
        <Select value={v.consegna || "nessuna"} onValueChange={(c) => setV((x) => ({ ...x, consegna: c === "nessuna" ? "" : c }))}>
          <SelectTrigger id="piano-consegna" aria-describedby="piano-consegna-nota" data-testid="piano-consegna">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="nessuna">Non l'ha detta</SelectItem>
            {mesiDiConsegna(v.consegna).map((m) => (
              <SelectItem key={m} value={m}>
                {meseConsegna(m)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p id="piano-consegna-nota" className="text-xs text-muted-foreground">
          Anche solo il mese: chi fa l'ordine la trova accanto a «Fai l'ordine».
        </p>
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-group border-t pt-group">
        <p className={cn("text-sm", problema ? "text-warning-foreground" : "text-muted-foreground")} data-testid="piano-somma">
          {problema || `Le rate fanno ${eur(totale)}, come il totale.`}
        </p>
        <div className="flex gap-inside">
          {onAnnulla ? (
            <Button type="button" variant="outline" onClick={onAnnulla}>
              {etichettaAnnulla}
            </Button>
          ) : null}
          <Button type="submit" disabled={Boolean(problema) || busy} data-testid="piano-salva">
            {busy ? "Salvo…" : etichettaSalva}
          </Button>
        </div>
      </div>
    </form>
  );
}

function Piano({ d, ruolo, onAggiornato, className }) {
  const vuoto = !d.piano.length;
  const [modifica, setModifica] = useState(false);
  const puo = d.puo.modificare_piano;
  const editing = puo && (modifica || vuoto);
  const vediIncassi = ruolo === "titolare" || ruolo === "amministrazione";
  const incassatoTot = d.piano.reduce((t, r) => t + (centesimiDaApi(r.incassato) || 0), 0);
  const quotaIncassata = Math.min(100, Math.round((incassatoTot / (centesimiDaApi(d.somma_piano) || 1)) * 100));
  const formula = [FORMULE_SALDO[d.contratto.saldo_modalita]?.label,
    d.contratto.consegna_voluta ? `consegna voluta: ${meseConsegna(d.contratto.consegna_voluta)}` : null]
    .filter(Boolean).join(" · ");

  return (
    <SezioneCard
      titolo="Piano di pagamento"
      descrizione={editing ? "Gli acconti e il saldo. La somma deve fare il totale: lo controlla anche il database." : formula}
      testId="contratto-piano"
      className={className}
      azioni={
        puo && !editing ? (
          <Button variant="outline" size="sm" onClick={() => setModifica(true)} data-testid="piano-modifica">
            Modifica
          </Button>
        ) : null
      }
    >
      {editing ? (
        <PianoForm
          key={d.contratto.updated_at}
          d={d}
          onSalvato={(nuovo) => {
            setModifica(false);
            onAggiornato(nuovo);
          }}
          onAnnulla={vuoto ? null : () => setModifica(false)}
        />
      ) : vuoto ? (
        <p className="text-ink-2">Il piano non c'è ancora. Lo scrive il venditore o l'amministrazione.</p>
      ) : (
        <div data-testid="piano-tabella">
          {vediIncassi ? (
            <div className="mb-3" data-testid="piano-incassato">
              <p>
                <span className="font-bold tabular-nums text-ink-strong">{eur(incassatoTot / 100)}</span>
                <span className="text-ink-2"> incassati su {eur(d.somma_piano)}</span>
              </p>
              <div className="mt-1.5 h-2 overflow-hidden rounded-full bg-surface-3" aria-hidden>
                <div className="h-full rounded-full bg-brand" style={{ width: `${quotaIncassata}%` }} />
              </div>
            </div>
          ) : null}
          <ul className="divide-y divide-line-subtle">
            {d.piano.map((r) => {
              const pagata = vediIncassi && centesimiDaApi(r.incassato) >= centesimiDaApi(r.importo);
              return (
                <li key={r.id} className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 py-3">
                  <div className="min-w-0 flex-1 basis-48">
                    <p className="font-semibold text-ink-strong">{r.causale || RATE[r.tipo]}</p>
                    <p className="text-small text-ink-2">{r.scadenza ? `Entro il ${dataLunga(r.scadenza)}` : "Data da fissare"}</p>
                    {vediIncassi && centesimiDaApi(r.incassato) > 0 ? (
                      <p className={cn("text-small", pagata ? "font-semibold text-success-ink" : "text-ink-2")}>
                        {pagata ? "Pagata" : `Incassati ${eur(r.incassato)}`}
                      </p>
                    ) : null}
                  </div>
                  <span className="whitespace-nowrap font-bold tabular-nums text-ink-strong">{eur(r.importo)}</span>
                </li>
              );
            })}
            <li className="flex items-center justify-between gap-4 py-3">
              <span className="font-semibold text-ink-strong">Totale</span>
              <span className="whitespace-nowrap font-bold tabular-nums text-ink-strong">{eur(d.somma_piano)}</span>
            </li>
          </ul>
        </div>
      )}
    </SezioneCard>
  );
}

const CAMPI_CLIENTE = [
  ["Codice fiscale", (c) => c.codice_fiscale],
  ["Indirizzo", (c) => [c.indirizzo, [c.cap, c.citta, c.provincia ? `(${c.provincia})` : null].filter(Boolean).join(" ")].filter(Boolean).join(", ")],
  ["Telefono", (c) => c.telefono],
  ["Email", (c) => c.email],
];

function Cliente({ d, className }) {
  const c = d.cliente || {};
  const mancano = CAMPI_CLIENTE.filter(([, f]) => !f(c)).map(([n]) => n.toLowerCase());
  return (
    <SezioneCard
      titolo="Cliente"
      descrizione={d.contratto.cliente}
      testId="contratto-cliente"
      className={className}
      azioni={
        <Button asChild variant="ghost" size="sm">
          <Link to={`/clienti/${d.contratto.cliente_id}`}>Apri la scheda</Link>
        </Button>
      }
    >
      <dl className="grid gap-inside">
        {CAMPI_CLIENTE.map(([nome, f]) => (
          <div key={nome} className="grid grid-cols-[8rem_minmax(0,1fr)] gap-inside">
            <dt className="text-ink-2">{nome}</dt>
            <dd className={cn("break-words", f(c) ? "text-ink" : "text-ink-3")}>{f(c) || "—"}</dd>
          </div>
        ))}
      </dl>
      {mancano.length && !d.contratto.esterno ? (
        <p className="mt-group text-small text-ink-2">
          Sul foglio {mancano.length === 1 ? "resta una riga" : "restano delle righe"} da scrivere a penna ({mancano.join(", ")}).
          {d.contratto.stato === "bozza"
            ? mancano.length === 1
              ? " Se lo aggiungi alla scheda del cliente prima di passare a «da firmare», esce già stampato."
              : " Se li aggiungi alla scheda del cliente prima di passare a «da firmare», escono già stampati."
            : ""}
        </p>
      ) : null}
    </SezioneCard>
  );
}

async function apriPdf(id, numero) {
  // La finestra si apre SUBITO, dentro il clic: aperta dopo l'attesa del PDF,
  // il browser la blocca come popup.
  const finestra = window.open("", "_blank");
  try {
    const r = await api.get(`/contratti/${id}/pdf`, { responseType: "blob" });
    const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
    if (finestra) finestra.location.href = url;
    else {
      const a = document.createElement("a");
      a.href = url;
      a.download = `Contratto-${String(numero || "bozza").replace("/", "-")}.pdf`;
      a.click();
    }
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  } catch (e) {
    if (finestra) finestra.close();
    toast.error("Il PDF non si è aperto: riprova");
  }
}

function DettaglioContratto({ id }) {
  const { profile } = useAuth();
  const ruolo = profile?.ruolo;
  const [d, setD] = useState(null);
  const [errore, setErrore] = useState(null);
  const [busy, setBusy] = useState(false);
  const mia = useRef(0);

  const carica = useCallback(() => {
    const n = ++mia.current;
    api
      .get(`/contratti/${id}`)
      .then((r) => n === mia.current && (setD(r.data), setErrore(null)))
      .catch((e) => n === mia.current && setErrore(e?.response?.status === 404 ? "Contratto non trovato, o non è tuo." : messaggioErrore(e)));
  }, [id]);
  useEffect(carica, [carica]);

  const cambia = async (body) => {
    setBusy(true);
    try {
      const r = await api.post(`/contratti/${id}/stato`, body);
      const { esito, ...resto } = r.data;
      setD(resto);
      if (esito?.piano === "saldo_aggiornato") {
        toast.success("Pronto da firmare: il preventivo era cambiato, prezzo e piano ora lo seguono");
      } else {
        toast.success(`Contratto: ${statoContratto(resto.contratto.stato).label.toLowerCase()}`);
      }
      return true;
    } catch (e) {
      toast.error(messaggioErrore(e));
      return false;
    } finally {
      setBusy(false);
    }
  };

  const scegli = async (configurazione_id) => {
    setBusy(true);
    try {
      const r = await api.post(`/contratti/${id}/configurazione`, { configurazione_id });
      const { esito, ...resto } = r.data;
      setD(resto);
      if (esito?.piano === "saldo_aggiornato") toast.success("Configurazione cambiata: il piano segue il nuovo totale");
      else if (esito?.piano === "piano_da_rifare") toast.warning("Configurazione cambiata: gli acconti superavano il nuovo totale, il piano va rifatto");
      else toast.success("Configurazione scelta");
    } catch (e) {
      toast.error(messaggioErrore(e));
    } finally {
      setBusy(false);
    }
  };

  const back = { to: "/contratti", label: "Contratti", testId: "contratto-indietro" };
  if (errore) {
    return (
      <section className="grid grid-cols-[minmax(0,1fr)] gap-card" data-testid="page-contratto">
        <PageHeader back={back} title="Non disponibile" />
        <ErroreInline>{errore}</ErroreInline>
      </section>
    );
  }
  if (!d) {
    return (
      <section className="grid grid-cols-[minmax(0,1fr)] gap-card" data-testid="page-contratto" aria-busy="true">
        <PageHeader back={back} title="…" />
        <ListSkeleton rows={4} label="Carico il contratto…" />
      </section>
    );
  }
  const k = d.contratto;
  const sottotitolo = [k.configurazione, k.sede ? `sede di ${k.sede}` : null, k.venditore].filter(Boolean).join(" · ");
  return (
    <section className="grid grid-cols-[minmax(0,1fr)] gap-card" data-testid="page-contratto">
      <PageHeader
        back={back}
        title={k.cliente || "Cliente"}
        description={
          k.esterno ? (
            <>
              <StatusBadge className="mr-inside align-middle" data-testid="contratto-fuori">Fatto fuori</StatusBadge>
              {[`Contratto n. ${k.numero}`, sottotitolo].filter(Boolean).join(" · ")}
            </>
          ) : (
            [`Contratto n. ${k.numero}`, sottotitolo].filter(Boolean).join(" · ")
          )
        }
        actions={
          k.esterno ? (
            // Il foglio firmato è l'unico contratto che c'è: non si stampa, si apre.
            d.firmato ? (
              <Button variant="outline" onClick={() => apriVersione(d.firmato.id).catch(() => toast.error("Il file non si è aperto: riprova"))} data-testid="apri-firmato">
                <FileCheck />
                Apri il firmato
              </Button>
            ) : null
          ) : (
            <Button variant="outline" onClick={() => apriPdf(k.id, k.numero)} data-testid="stampa-pdf">
              <Printer />
              Stampa
            </Button>
          )
        }
      />
      {/* Ogni riquadro si monta UNA volta. Sul telefono una colonna sola, nell'ordine
          stato → prezzo → piano → cliente (le due colonne sono `contents`, e
          decide `order`); da `lg` due colonne vere, lo stato in alto a destra. */}
      <div className="grid grid-cols-[minmax(0,1fr)] gap-card lg:grid-cols-[minmax(0,1fr)_22rem] lg:items-start">
        <div className="contents lg:grid lg:gap-card">
          <Prezzo d={d} ruolo={ruolo} onScegli={scegli} busy={busy} className="order-2" />
          <Piano d={d} ruolo={ruolo} onAggiornato={setD} className="order-3" />
        </div>
        <div className="contents lg:grid lg:gap-card">
          <PassoStato d={d} onCambia={cambia} busy={busy} className="order-1" />
          <Cliente d={d} className="order-4" />
        </div>
      </div>
    </section>
  );
}

export default function ContrattiPage() {
  const [params, setParams] = useSearchParams();
  const id = params.get("c");
  const apri = useCallback(
    (nuovo) => {
      const p = new URLSearchParams(params);
      p.set("c", nuovo);
      setParams(p);
    },
    [params, setParams],
  );
  if (id) return <DettaglioContratto key={id} id={id} />;
  return <ElencoContratti apri={apri} />;
}
